import { atom, useAtomValue, useSetAtom } from 'jotai';
import React, { FC, Key, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';

import { useIsAuthenticated } from './auth/authentication';
import { GlobalNavigationDrawer } from './components/GlobalNavigationDrawer';
import { NavigationDrawer } from './components/NavigationDrawer/NavigationDrawer';
import { AxlePage } from './components/layout/AxlePage';
import { AxleErrorBoundary, ErrorFallback } from './fetching/errorBoundaries';
import { AxleSuspense } from './fetching/suspense';
import { NotFoundPage } from './pages/NotFoundPage';
import { FeatureFlag } from './types/featureFlag';
import { isLocal } from './utils/axleEnvironment';
import { Component, DefiniteComponent } from './utils/reactTypes';
import { useFeatureFlagEnabled, useFeatureFlagsEnabled } from './utils/useFeatureFlagEnabled';

// Lazy load all page components for code splitting
const AllVisitsPage = lazy(() =>
    import('./pages/AllVisitsPage/AllVisitsPage').then((m) => ({ default: m.AllVisitsPage })),
);
const BlocksPage = lazy(() =>
    import('./pages/BlocksPage').then((m) => ({ default: m.BlocksPage })),
);
const BookingFlow = lazy(() =>
    import('./pages/BookingFlow/BookingFlow').then((m) => ({ default: m.BookingFlow })),
);
const CreateServiceAreaPage = lazy(() =>
    import('./pages/CRUDServiceAreaPages').then((m) => ({ default: m.CreateServiceAreaPage })),
);
const EditOrViewServiceAreaPage = lazy(() =>
    import('./pages/CRUDServiceAreaPages').then((m) => ({ default: m.EditOrViewServiceAreaPage })),
);
const ClinicianDetailsPage = lazy(() =>
    import('./pages/ClinicianDetailsPage/ClinicianDetailsPage').then((m) => ({
        default: m.ClinicianDetailsPage,
    })),
);
const CliniciansPage = lazy(() =>
    import('./pages/CliniciansPage/CliniciansPage').then((m) => ({
        default: m.CliniciansPage,
    })),
);
const DashboardPage = lazy(() =>
    import('./pages/DashboardPage/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const DemandPage = lazy(() =>
    import('./pages/DemandPage/DemandPage').then((m) => ({ default: m.DemandPage })),
);
const ForgotPasswordPage = lazy(() =>
    import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const GoogleMapTestingShimPage = lazy(() =>
    import('./components/mapping/GoogleMap/GoogleMapTestingShimPage').then((m) => ({
        default: m.GoogleMapTestingShimPage,
    })),
);
const InsightsPage = lazy(() =>
    import('./pages/InsightsPage').then((m) => ({ default: m.InsightsPage })),
);
const IntakeCapacityPage = lazy(() =>
    import('./pages/IntakeCapacityPage').then((m) => ({ default: m.IntakeCapacityPage })),
);
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const MangleDbPage = lazy(() =>
    import('./pages/MangleDbPage').then((m) => ({ default: m.MangleDbPage })),
);
const OrderDetailsPage = lazy(() =>
    import('./pages/OrderDetailsPage/OrderDetailsPage').then((m) => ({
        default: m.OrderDetailsPage,
    })),
);
const OrdersPage = lazy(() =>
    import('./pages/OrdersPage/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const PatientDetailsPage = lazy(() =>
    import('./pages/PatientDetailsPage/PatientDetailsPage').then((m) => ({
        default: m.PatientDetailsPage,
    })),
);
const PatientPortalVisitsPage = lazy(() =>
    import('./pages/PatientPortalPages/PatientPortalVisitsPage').then((m) => ({
        default: m.PatientPortalVisitsPage,
    })),
);
const PatientsPage = lazy(() =>
    import('./pages/PatientsPage/PatientsPage').then((m) => ({ default: m.PatientsPage })),
);
const ReportsPage = lazy(() =>
    import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const ResetPasswordPage = lazy(() =>
    import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const RouteOptimizerAdminPage = lazy(() =>
    import('./pages/RouteOptimizerAdminPage/RouteOptimizerAdminPage').then((m) => ({
        default: m.RouteOptimizerAdminPage,
    })),
);
const RouteOptimizerPage = lazy(() =>
    import('./pages/RouteOptimizerPage/RouteOptimizerPage').then((m) => ({
        default: m.RouteOptimizerPage,
    })),
);
const SSOCallbackPage = lazy(() =>
    import('./pages/SSOCallbackPage').then((m) => ({ default: m.SSOCallbackPage })),
);
const SSOLoginPage = lazy(() =>
    import('./pages/SSOLoginPage').then((m) => ({ default: m.SSOLoginPage })),
);
const ClinicianSchedulesPage = lazy(() =>
    import('./pages/SchedulePage/ClinicianSchedulesPage').then((m) => ({
        default: m.ClinicianSchedulesPage,
    })),
);
const ScheduleVisitPage = lazy(() =>
    import('./pages/ScheduleVisitPages/ScheduleVisitPage').then((m) => ({
        default: m.ScheduleVisitPage,
    })),
);
const ScratchPage = lazy(() =>
    import('./pages/ScratchPage').then((m) => ({ default: m.ScratchPage })),
);
const ServiceAreasPage = lazy(() =>
    import('./pages/ServiceAreasPage').then((m) => ({ default: m.ServiceAreasPage })),
);
const UserSettingsPage = lazy(() =>
    import('./pages/UserSettingsPage/UserSettingsPage').then((m) => ({
        default: m.UserSettingsPage,
    })),
);
const SettingsPage = lazy(() =>
    import('./pages/SettingsPage/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const SupplyPage = lazy(() =>
    import('./pages/SupplyPage/SupplyPage').then((m) => ({ default: m.SupplyPage })),
);
const TaskDetailsPage = lazy(() =>
    import('./pages/TaskDetailsPage').then((m) => ({ default: m.TaskDetailsPage })),
);
const TerritoriesPage = lazy(() =>
    import('./pages/TerritoriesPage/TerritoriesPage').then((m) => ({ default: m.TerritoriesPage })),
);
const VisitBookingPage = lazy(() =>
    import('./pages/VisitBooking/VisitBookingPage').then((m) => ({ default: m.VisitBookingPage })),
);
const VisitDetailsPage = lazy(() =>
    import('./pages/VisitDetailsPage/VisitDetailsPage').then((m) => ({
        default: m.VisitDetailsPage,
    })),
);
const VisitTravelPathDebugPage = lazy(() =>
    import('./pages/VisitDetailsPage/VisitTravelPathDebugPage').then((m) => ({
        default: m.VisitTravelPathDebugPage,
    })),
);
const VisitOrderDetailsPage = lazy(() =>
    import('./pages/VisitOrderDetailsPage').then((m) => ({ default: m.VisitOrderDetailsPage })),
);
const VisitOrdersPage = lazy(() =>
    import('./pages/VisitOrdersPage').then((m) => ({ default: m.VisitOrdersPage })),
);
const VisitRequestDetailsPage = lazy(() =>
    import('./pages/VisitRequestDetailsPage/VisitRequestDetailsPage').then((m) => ({
        default: m.VisitRequestDetailsPage,
    })),
);
const LegacyPatientDetailsPage = lazy(() =>
    import('./pages/legacy/LegacyPatientDetailsPage/LegacyPatientDetailsPage').then((m) => ({
        default: m.LegacyPatientDetailsPage,
    })),
);
const LegacyPatientsPage = lazy(() =>
    import('./pages/legacy/LegacyPatientsPage/LegacyPatientsPage').then((m) => ({
        default: m.LegacyPatientsPage,
    })),
);
const LegacyVisitsPage = lazy(() =>
    import('./pages/legacy/LegacyVisitsPage/LegacyVisitsPage').then((m) => ({
        default: m.LegacyVisitsPage,
    })),
);
const QueryTestingShimPage = lazy(() =>
    import('./pages/shims/QueryTestShimPage').then((m) => ({ default: m.QueryTestingShimPage })),
);
const SentryTestingShimPage = lazy(() =>
    import('./pages/shims/SentryTestingShimPage').then((m) => ({
        default: m.SentryTestingShimPage,
    })),
);

type RouteProps = {
    path: string;
    condition?: boolean;
    withNavigationDrawer?: boolean;
    component: React.LazyExoticComponent<() => Component> | (() => Component);
};

/* Route that wraps pages inside a page-level Suspense and ErrorBoundary. */
const UnauthenticatedRoute = ({
    path,
    condition = true,
    withNavigationDrawer = false,
    component: Component,
}: RouteProps): DefiniteComponent => {
    const hasGlobalNavigationV2 = useFeatureFlagEnabled(FeatureFlag.GLOBAL_NAVIGATION_V2, {
        skip: !withNavigationDrawer,
    });

    if (!condition) {
        return <NotFoundPage />;
    }
    // Use the route path as the key to reset error boundary on route changes
    const componentKey: Key = path;
    return (
        <>
            {withNavigationDrawer ? (
                hasGlobalNavigationV2 ? (
                    <GlobalNavigationDrawer />
                ) : (
                    <NavigationDrawer />
                )
            ) : null}
            {/* This is page-level error boundary. It makes it so that you can still switch
             *   pages with the NavigationDrawer even if a page encounters an error.
             *
             * We need to set the key= property so that the error boundary is reset/re-rendered
             *  when you go to a new page; otherwise it will stay stuck in error mode.
             *  See: https://stackoverflow.com/q/59056211/1292652
             */}
            <AxleErrorBoundary
                key={componentKey}
                reload={true}
                fallback={
                    <AxlePage noNavDrawer={!withNavigationDrawer}>
                        <ErrorFallback />
                    </AxlePage>
                }
            >
                {/* This is page-level suspense. It makes it so when you are switching between
                 *   pages, the NavigationDrawer doesn't disappear while the new page is loading.
                 *
                 *  Pages are encouraged to have their own inner Suspense elements!!
                 *   It looks prettier if the title of the page renders immediately but the details
                 *   below them have a spinner, than if the title takes time to load too.
                 */}
                <AxleSuspense
                    customFallback={<AxlePage noNavDrawer={!withNavigationDrawer} loading />}
                >
                    <Component />
                </AxleSuspense>
            </AxleErrorBoundary>
        </>
    );
};

// When user has multiple tabs, logging out of one logs out of all
// We do not want sibling tabs to redirect to current page
// This is to prevent 404s if logging into a different account with different permissions
const isEverLoggedInAtom = atom(false);

const useRedirectRoute = () => {
    const isAuthenticated = useIsAuthenticated();
    const isEverLoggedIn = useAtomValue(isEverLoggedInAtom);
    const location = useLocation();

    if (isEverLoggedIn && !isAuthenticated) {
        return '';
    }

    const redirectEncoded = encodeURIComponent(location.pathname + location.search);
    // %2F is encoded "/" and does not need a redirect
    return redirectEncoded !== '%2F' ? `?redirect=${redirectEncoded}` : '';
};

const RedirectToSignin: FC = () => {
    const redirectRoute = useRedirectRoute();

    return <Navigate to={`/sign-in${redirectRoute}`} replace />;
};

function LegacyPatientRedirect() {
    const { patientId } = useParams();
    return <Navigate to={`/patients/${patientId}`} replace />;
}

function PatientDetailsIndexRedirect() {
    const { patientId } = useParams();
    return <Navigate to={`/patients/${patientId}/scheduling`} replace />;
}

function VisitRescheduleRedirect() {
    const { visitId } = useParams();
    return <Navigate to={`/visits/${visitId}/schedule/`} replace />;
}

/* Wrapper rendered inside <Route element={}> for authenticated pages. */
const AuthenticatedWrapper = (props: RouteProps): DefiniteComponent => {
    const isAuthenticated = useIsAuthenticated();

    if (!isAuthenticated) {
        return <RedirectToSignin />;
    }

    return <UnauthenticatedRoute withNavigationDrawer={true} {...props} />;
};

/* Define the app routes, and return the current app page. */
export const RootRoutes = () => {
    const isAuthenticated = useIsAuthenticated();
    const setIsEverLoggedIn = useSetAtom(isEverLoggedInAtom);

    useEffect(() => {
        if (isAuthenticated) {
            setIsEverLoggedIn(true);
        }
    }, [isAuthenticated, setIsEverLoggedIn]);

    const { hasNewVisitsList, hasNewPatientsList, hasNewPatientsDetails, hasNewCliniciansList } =
        useFeatureFlagsEnabled(
            {
                hasNewVisitsList: FeatureFlag.VISITS_LIST_PAGE_V2,
                hasNewPatientsList: FeatureFlag.PATIENTS_LIST_PAGE_V2,
                hasNewPatientsDetails: FeatureFlag.PATIENT_DETAILS_PAGE_V2,
                hasNewCliniciansList: FeatureFlag.CLINICIANS_LIST_PAGE_V2,
            },
            { skip: !isAuthenticated },
        );
    return (
        <Routes>
            {/* Dashboard is the root route page, and will internally automatically redirect to the visits page if the dashboard feature flag is not enabled */}
            <Route path='/' element={<AuthenticatedWrapper path='/' component={DashboardPage} />} />

            {/* Patient portal pages (unauthenticated). */}
            <Route
                path='/patient-portal/:patientUuid/visits'
                element={
                    <UnauthenticatedRoute
                        path='/patient-portal/:patientUuid/visits'
                        component={PatientPortalVisitsPage}
                    />
                }
            />

            {/* Login pages (unauthenticated). */}
            <Route
                path='/sign-in'
                element={<UnauthenticatedRoute path='/sign-in' component={LoginPage} />}
            />
            <Route
                path='/reset-password'
                element={
                    <UnauthenticatedRoute path='/reset-password' component={ResetPasswordPage} />
                }
            />
            <Route
                path='/forgot-password'
                element={
                    <UnauthenticatedRoute path='/forgot-password' component={ForgotPasswordPage} />
                }
            />
            <Route
                path='/sso'
                element={<UnauthenticatedRoute path='/sso' component={SSOLoginPage} />}
            />
            <Route
                path='/sso-callback'
                element={<UnauthenticatedRoute path='/sso-callback' component={SSOCallbackPage} />}
            />

            {/* Partner admin pages (authenticated). */}
            {/* Note: The detail page routes need to come before the list routes, otherwise it doesn't work.
                Not sure why, since we are using `exact={true}` to define the routes, but be aware. */}
            {/* For simplicity, let's make the ordering here match the ordering of pages in the navigation drawer. */}
            <Route
                path='/visits/:visitId/schedule/'
                element={
                    <AuthenticatedWrapper
                        path='/visits/:visitId/schedule/'
                        component={ScheduleVisitPage}
                    />
                }
            />
            <Route path='/visits/reschedule/:visitId' element={<VisitRescheduleRedirect />} />
            <Route
                path='/visits/create'
                element={
                    <AuthenticatedWrapper path='/visits/create' component={VisitBookingPage} />
                }
            />
            <Route
                path='/visits/:visitId'
                element={
                    <AuthenticatedWrapper path='/visits/:visitId' component={VisitDetailsPage} />
                }
            />
            <Route
                path='/visits'
                element={
                    <AuthenticatedWrapper
                        path='/visits'
                        component={hasNewVisitsList ? AllVisitsPage : LegacyVisitsPage}
                    />
                }
            />
            <Route
                path='/visit-requests/:visitRequestId'
                element={
                    <AuthenticatedWrapper
                        path='/visit-requests/:visitRequestId'
                        component={VisitRequestDetailsPage}
                    />
                }
            />
            <Route
                path='/route-optimizer/admin'
                element={
                    <AuthenticatedWrapper
                        path='/route-optimizer/admin'
                        component={RouteOptimizerAdminPage}
                    />
                }
            />
            <Route
                path='/route-optimizer'
                element={
                    <AuthenticatedWrapper path='/route-optimizer' component={RouteOptimizerPage} />
                }
            />
            <Route
                path='/booking'
                element={<AuthenticatedWrapper path='/booking' component={BookingFlow} />}
            />
            {/* Bare /patients/:patientId redirects to the scheduling tab so the inner tabs
                always render with a matching value (avoids a MUI Tabs warning). */}
            {hasNewPatientsDetails && (
                <Route path='/patients/:patientId' element={<PatientDetailsIndexRedirect />} />
            )}
            {/* When hasNewPatientsDetails, PatientDetailsPage has sub-routes so needs /* */}
            <Route
                path={hasNewPatientsDetails ? '/patients/:patientId/*' : '/patients/:patientId'}
                element={
                    <AuthenticatedWrapper
                        path='/patients/:patientId'
                        component={
                            hasNewPatientsDetails ? PatientDetailsPage : LegacyPatientDetailsPage
                        }
                    />
                }
            />
            {/* Redirect legacy patient detail subroutes to base page */}
            {!hasNewPatientsDetails && (
                <Route path='/patients/:patientId/:subroute' element={<LegacyPatientRedirect />} />
            )}
            <Route
                path='/patients'
                element={
                    <AuthenticatedWrapper
                        path='/patients'
                        component={hasNewPatientsList ? PatientsPage : LegacyPatientsPage}
                    />
                }
            />

            {/* Direct URLs to legacy versions of pages when feature flags are enabled,
            for ease of debugging and reference. */}
            {hasNewPatientsDetails && (
                <Route
                    path='/legacy/patients/:patientId'
                    element={
                        <AuthenticatedWrapper
                            path='/legacy/patients/:patientId'
                            component={LegacyPatientDetailsPage}
                        />
                    }
                />
            )}
            {hasNewPatientsList && (
                <Route
                    path='/legacy/patients'
                    element={
                        <AuthenticatedWrapper
                            path='/legacy/patients'
                            component={LegacyPatientsPage}
                        />
                    }
                />
            )}
            {hasNewVisitsList && (
                <Route
                    path='/legacy/visits'
                    element={
                        <AuthenticatedWrapper path='/legacy/visits' component={LegacyVisitsPage} />
                    }
                />
            )}
            {hasNewCliniciansList && (
                <Route
                    path='/legacy/clinicians'
                    element={
                        <AuthenticatedWrapper path='/legacy/clinicians' component={SupplyPage} />
                    }
                />
            )}
            {/* New Orders Page routes - will check for feature flag inside the page to verify access. */}
            <Route
                path='/orders/:orderId'
                element={
                    <AuthenticatedWrapper path='/orders/:orderId' component={OrderDetailsPage} />
                }
            />
            <Route
                path='/orders'
                element={<AuthenticatedWrapper path='/orders' component={OrdersPage} />}
            />
            {/* Old Orders Page routes */}
            <Route
                path='/visit-orders/:visitOrderId'
                element={
                    <AuthenticatedWrapper
                        path='/visit-orders/:visitOrderId'
                        component={VisitOrderDetailsPage}
                    />
                }
            />
            <Route
                path='/visit-orders'
                element={<AuthenticatedWrapper path='/visit-orders' component={VisitOrdersPage} />}
            />
            {/*
            Q: Why do we prefix the task details route with `/clinicians/...`?
            A: So that the sidebar label for "Clinicians" is highlighted when on the task
               details page. The task detail page is only gotten to via a Clinician details
               page, and kind of belongs semantically in the "Clinicians" sidebar group of
               pages.

            Note: Because of the `/clinicians/...` prefix, this route must come before the
            `/clinicians` route, otherwise the route matching gets weird, and we get a blank page.
            */}
            <Route
                path='/clinicians/tasks/:taskId'
                element={
                    <AuthenticatedWrapper
                        path='/clinicians/tasks/:taskId'
                        component={TaskDetailsPage}
                    />
                }
            />
            <Route
                path='/clinicians/:clinicianId'
                element={
                    <AuthenticatedWrapper
                        path='/clinicians/:clinicianId'
                        component={ClinicianDetailsPage}
                    />
                }
            />
            <Route
                path='/clinicians'
                element={
                    <AuthenticatedWrapper
                        path='/clinicians'
                        component={hasNewCliniciansList ? CliniciansPage : SupplyPage}
                    />
                }
            />
            <Route
                path='/schedules/*'
                element={
                    <AuthenticatedWrapper path='/schedules' component={ClinicianSchedulesPage} />
                }
            />
            <Route
                path='/territories'
                element={<AuthenticatedWrapper path='/territories' component={TerritoriesPage} />}
            />
            <Route
                path='/services'
                element={<AuthenticatedWrapper path='/services' component={DemandPage} />}
            />
            <Route
                path='/service-areas/create'
                element={
                    <AuthenticatedWrapper
                        path='/service-areas/create'
                        component={CreateServiceAreaPage}
                    />
                }
            />
            <Route
                path='/service-areas/:serviceAreaId'
                element={
                    <AuthenticatedWrapper
                        path='/service-areas/:serviceAreaId'
                        component={EditOrViewServiceAreaPage}
                    />
                }
            />
            <Route
                path='/service-areas'
                element={
                    <AuthenticatedWrapper path='/service-areas' component={ServiceAreasPage} />
                }
            />
            <Route
                path='/blocks'
                element={<AuthenticatedWrapper path='/blocks' component={BlocksPage} />}
            />
            <Route
                path='/reports'
                element={<AuthenticatedWrapper path='/reports' component={ReportsPage} />}
            />
            <Route
                path='/insights'
                element={<AuthenticatedWrapper path='/insights' component={InsightsPage} />}
            />
            <Route
                path='/user-settings/*'
                element={
                    <AuthenticatedWrapper path='/user-settings' component={UserSettingsPage} />
                }
            />
            <Route
                path='/settings'
                element={<AuthenticatedWrapper path='/settings' component={SettingsPage} />}
            />
            <Route
                path='/patient-intake'
                element={
                    <AuthenticatedWrapper path='/patient-intake' component={IntakeCapacityPage} />
                }
            />
            {/* Some development-only test paths (authenticated). */}
            <Route
                path='/.../map'
                element={
                    <AuthenticatedWrapper
                        path='/.../map'
                        component={GoogleMapTestingShimPage}
                        condition={isLocal}
                    />
                }
            />
            {/* formerly isLocal */}
            <Route
                path='/.../scratch'
                element={<AuthenticatedWrapper path='/.../scratch' component={ScratchPage} />}
            />
            <Route
                path='/.../query'
                element={
                    <AuthenticatedWrapper
                        path='/.../query'
                        component={QueryTestingShimPage}
                        condition={isLocal}
                    />
                }
            />

            {/* Some development and production test paths (authenticated). */}
            <Route
                path='/.../sentry'
                element={
                    <AuthenticatedWrapper path='/.../sentry' component={SentryTestingShimPage} />
                }
            />
            <Route
                path='/.../visits/:visitId/travel-path-debug'
                element={
                    <AuthenticatedWrapper
                        path='/.../visits/:visitId/travel-path-debug'
                        component={VisitTravelPathDebugPage}
                    />
                }
            />
            {/* This path is hard-coded into the backend. */}
            <Route
                path='/.../mangledb'
                element={<AuthenticatedWrapper path='/.../mangledb' component={MangleDbPage} />}
            />

            {/* Fallback for no matching route (unauthenticated). */}
            <Route path='*' element={<NotFoundPage />} />
        </Routes>
    );
};
